import { Box, Divider, Popover } from "@mui/material";
import { useMemo, useState } from "react";
import ScrollableFlexContainer from "../../ScrollableFlexContainer";
import PdfDocumentWrapper from "./PdfDocumentWrapper";
import PdfPagesContainer from "./PdfPagesContainer";
import PdfThumbnailsContainer from "./PdfThumbnailsContainer";
import PdfViewerControls from "./PdfViewerControls";
import { usePdfViewer } from "./pdfViewerState";
import { pdfViewerStyles } from "./pdfViewerStyles";

interface Props {
  fileSrc: string;
  fileName: string;
}

const PdfPreview = ({ fileSrc, fileName }: Props) => {
  const {
    viewerState,
    containerEl,
    setContainerEl,
    pageRefs,
    containerSize,
    handleScroll,
    navigateToPage,
    toggleThumbnails,
    handleDocumentLoaded,
    updateLoadingProgress,
    zoomIn,
    zoomOut,
    rotateLeft,
    rotateRight,
  } = usePdfViewer();

  const [titleEl, setTitleEl] = useState<HTMLDivElement | null>(null);

  const containerOrientation = useMemo(
    () =>
      containerSize?.height && containerSize?.width
        ? containerSize.height > containerSize.width
          ? "portrait"
          : "landscape"
        : undefined,
    [containerSize?.height, containerSize?.width]
  );

  const pageHeight = useMemo(
    () => (containerSize?.height && containerOrientation === "landscape" ? containerSize?.height - 48 : undefined),
    [containerOrientation, containerSize?.height]
  );

  const pageWidth = useMemo(
    () => (containerSize?.width && containerOrientation === "portrait" ? containerSize?.width - 48 : undefined),
    [containerOrientation, containerSize?.width]
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Box ref={setTitleEl} sx={{ py: 1, px: 2.5 }}>
        <PdfViewerControls
          fileName={fileName}
          viewerState={viewerState}
          hideFileName
          onNavigateToPage={navigateToPage}
          onToggleThumbnails={toggleThumbnails}
          onZoomOut={zoomOut}
          onZoomIn={zoomIn}
          onRotateLeft={rotateLeft}
          onRotateRight={rotateRight}
        />
      </Box>
      <Divider />
      <Box ref={setContainerEl} sx={{ flex: 1, bgcolor: pdfViewerStyles.bgcolor }}>
        <ScrollableFlexContainer onScroll={handleScroll} sx={{ height: "100%" }}>
          <Box onContextMenu={(e) => e.preventDefault()} sx={{ height: "100%" }}>
            <PdfDocumentWrapper
              fileSrc={fileSrc}
              viewerState={viewerState}
              onDocumentLoad={handleDocumentLoaded}
              onLoadingProgress={updateLoadingProgress}
            >
              <Popover
                open={viewerState.showThumbnails}
                onClose={() => toggleThumbnails()}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                anchorEl={titleEl}
                sx={{ "& .MuiPopover-paper": { maxHeight: containerSize?.height } }}
              >
                <PdfThumbnailsContainer viewerState={viewerState} />
              </Popover>

              <PdfPagesContainer
                viewerState={viewerState}
                containerEl={containerEl}
                pageRefs={pageRefs}
                pageHeight={pageHeight}
                pageWidth={pageWidth}
              />
            </PdfDocumentWrapper>
          </Box>
        </ScrollableFlexContainer>
      </Box>
    </Box>
  );
};

export default PdfPreview;
